// =====================================================
// COMMON COMPONENTS
// =====================================================
import React, { useState } from 'react';

export const PageHeader = ({ title, description, actions }) => (
  <div className="page-header">
    <div className="page-title-area">
      <h2 className="page-title">{title}</h2>
      {description && <p className="page-description">{description}</p>}
    </div>
    {actions && <div className="page-actions">{actions}</div>}
  </div>
);

export const Card = ({ title, subtitle, children, actions, className = '' }) => (
  <div className={`card ${className}`}>
    {(title || actions) && (
      <div className="card-header">
        <div>
          {title && <div className="card-title">{title}</div>}
          {subtitle && <div className="card-subtitle">{subtitle}</div>}
        </div>
        {actions && <div>{actions}</div>}
      </div>
    )}
    {children}
  </div>
);

export const StatCard = ({ icon, label, value, change, color = 'blue', prefix = '' }) => (
  <div className={`stat-card ${color}`}>
    <div className={`stat-icon ${color}`}>{icon}</div>
    <div className="stat-value">{prefix}{typeof value === 'number' ? value.toLocaleString('en-IN', { maximumFractionDigits: 0 }) : value}</div>
    <div className="stat-label">{label}</div>
    {change && <div className={`stat-change ${change > 0 ? 'up' : 'down'}`}>{change > 0 ? '▲' : '▼'} {Math.abs(change)}%</div>}
  </div>
);

export const FormGroup = ({ label, required, children }) => (
  <div className="form-group">
    <label className="form-label">{label}{required && <span className="required">*</span>}</label>
    {children}
  </div>
);

export const Input = ({ ...props }) => <input className="form-input" {...props} />;

export const Select = ({ options = [], placeholder = 'Select', ...props }) => (
  <select className="form-select" {...props}>
    <option value="">{placeholder}</option>
    {options.map(o => (
      <option key={typeof o === 'string' ? o : o.value} value={typeof o === 'string' ? o : o.value}>
        {typeof o === 'string' ? o : o.label}
      </option>
    ))}
  </select>
);

export const Textarea = ({ ...props }) => <textarea className="form-textarea" {...props} />;

export const Button = ({ variant = 'primary', size = '', icon, children, ...props }) => (
  <button className={`btn btn-${variant} ${size ? 'btn-' + size : ''}`} {...props}>
    {icon && icon}{children}
  </button>
);

export const Badge = ({ color = 'blue', children }) => (
  <span className={`badge badge-${color}`}>{children}</span>
);

export const Tabs = ({ tabs, active, onChange }) => (
  <div className="tabs">
    {tabs.map(t => (
      <button key={t.id} className={`tab ${active === t.id ? 'active' : ''}`} onClick={() => onChange(t.id)}>
        {t.label}
      </button>
    ))}
  </div>
);

export const ActionButtons = ({ onEdit, onDelete, onPrint }) => (
  <div className="action-btns">
    {onEdit && (
      <button className="icon-btn" onClick={onEdit} title="Edit">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
      </button>
    )}
    {onPrint && (
      <button className="icon-btn" onClick={onPrint} title="Print">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
      </button>
    )}
    {onDelete && (
      <button className="icon-btn danger" onClick={onDelete} title="Delete">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
      </button>
    )}
  </div>
);

export const DataTable = ({ title, columns, data, onSearch, actions, footer }) => {
  const [query, setQuery] = useState('');
  const filtered = query && onSearch ? onSearch(query) : data;

  return (
    <div className="table-wrapper fade-in">
      <div className="table-header">
        <div className="table-title">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M3 15h18M9 3v18"/></svg>
          {title}
        </div>
        <div className="table-controls">
          {actions}
          <div className="search-input-wrap">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input className="search-input" placeholder="Search..." value={query} onChange={e => setQuery(e.target.value)} />
          </div>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>{columns.map(c => <th key={c.key}>{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={columns.length} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '32px' }}>No records found</td></tr>
            ) : filtered.map((row, i) => (
              <tr key={row.id || i}>
                {columns.map(c => (
                  <td key={c.key} className={c.className || ''}>
                    {c.render ? c.render(row) : row[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="table-footer">
        <span>Showing {filtered.length} of {data.length} entries</span>
        {footer}
      </div>
    </div>
  );
};

export const SectionDivider = () => <div className="section-divider" />;

export const Alert = ({ type = 'info', children }) => (
  <div className={`alert alert-${type}`}>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
    {children}
  </div>
);
